// PositionsTab — Tab 2: „Alle {COIN}-Positionen weltweit".
// Layout: DataTable mit Avatar-Stack + External-Link + Quality-Badge.
//
// EVE-Zugang: globaler `EvePresenceDrawer`, kein eigener Composer (Phase 4Q).
// Daten kommen aus useHlPositions(coin) — Page reicht uns hier nur `rows`
// rein. Solange leer, zeigt die Tabelle Empty-State.

import { useState } from 'react';
import {
  AvatarStack,
  DataTable,
  ExternalLinkButton,
  QualityBadge,
  type AvatarSpec,
  type DataTableColumn,
} from '@/components/ui/data-table';
import type { WalletQuality } from '@/lib/hl-quality';
import type { HyperliquidCoin } from './hyperliquid-metrics-bar';

export interface HlPositionRow {
  id: string;
  label?: string;
  address: string;
  sizeBtc: number;
  entry: number;
  mark: number;
  valueUsd: number;
  pnlPct: number;
  leverage: number;
  quality: WalletQuality;
  avatars?: AvatarSpec[];
  /** Optional Link-Anker (z.B. HL-Explorer). */
  explorerUrl?: string;
}

interface PositionsTabProps {
  coin: HyperliquidCoin;
  rows: ReadonlyArray<HlPositionRow>;
  /** Gesamt-Wallets im DB-Scan (für „X von Y") */
  totalWallets?: number;
  /** Wenn gesetzt: überschreibt den Default-Subtitle (z.B. Scan-Progress). */
  subtitle?: React.ReactNode;
  loading?: boolean;
  onScan?: () => void;
  onTrack?: (id: string) => void;
}

export function PositionsTab({
  coin,
  rows,
  totalWallets,
  subtitle,
  loading,
  onScan,
  onTrack,
}: PositionsTabProps) {
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const filteredRows = filterRows(rows, search);

  const columns: DataTableColumn<HlPositionRow>[] = [
    {
      key: 'select',
      header: (
        <input
          type="checkbox"
          aria-label="Alle auswählen"
          checked={filteredRows.length > 0 && filteredRows.every((r) => selected.has(r.id))}
          onChange={(e) => {
            setSelected(() =>
              e.target.checked ? new Set(filteredRows.map((r) => r.id)) : new Set(),
            );
          }}
          className="rounded border-white/20 bg-transparent"
        />
      ),
      cell: (r) => (
        <input
          type="checkbox"
          aria-label={`Auswählen ${r.label ?? r.address}`}
          checked={selected.has(r.id)}
          onChange={(e) => {
            setSelected((prev) => {
              const next = new Set(prev);
              if (e.target.checked) next.add(r.id);
              else next.delete(r.id);
              return next;
            });
          }}
          className="rounded border-white/20 bg-transparent"
        />
      ),
      thClassName: 'w-8',
      toggleable: false,
    },
    {
      key: 'wallet',
      header: 'Wallet',
      cell: (r) => (
        <div className="flex items-center gap-2">
          {r.avatars && r.avatars.length > 0 ? (
            <AvatarStack avatars={r.avatars} max={2} />
          ) : (
            <div className="grid size-6 place-items-center rounded-full bg-neutral-700 text-[10px] font-bold text-neutral-400">
              ?
            </div>
          )}
          <div className="min-w-0">
            {r.label ? (
              <div className="text-[12px] font-bold text-amber-400">{r.label}</div>
            ) : (
              <div className="text-[10px] text-neutral-500">unknown</div>
            )}
            <div className="flex items-center gap-1 font-mono text-[11px] text-neutral-500">
              {shortAddr(r.address)}
              {r.explorerUrl ? (
                <ExternalLinkButton href={r.explorerUrl} label="Wallet im Explorer" />
              ) : null}
            </div>
          </div>
        </div>
      ),
    },
    {
      key: 'size',
      header: `Size (${coin})`,
      align: 'right',
      cell: (r) => (
        <span className={r.sizeBtc >= 0 ? 'text-emerald-400' : 'text-rose-400'}>
          {r.sizeBtc >= 0 ? '+' : '−'}
          {Math.abs(r.sizeBtc).toFixed(3)}
        </span>
      ),
    },
    { key: 'entry', header: 'Entry', align: 'right', cell: (r) => `$${fmt(r.entry)}` },
    { key: 'mark', header: 'Mark', align: 'right', cell: (r) => `$${fmt(r.mark)}` },
    {
      key: 'value',
      header: 'Value',
      align: 'right',
      cell: (r) => <span className="font-bold text-amber-400">{fmtBig(r.valueUsd)}</span>,
    },
    {
      key: 'pnlPct',
      header: 'PnL %',
      align: 'right',
      cell: (r) => (
        <span className={r.pnlPct >= 0 ? 'text-emerald-400' : 'text-rose-400'}>
          {r.pnlPct >= 0 ? '+' : ''}
          {r.pnlPct.toFixed(2)}%
        </span>
      ),
    },
    {
      key: 'lev',
      header: 'Lev',
      align: 'right',
      cell: (r) => <span className="text-neutral-400">{r.leverage}×</span>,
    },
    {
      key: 'quality',
      header: 'Quality',
      align: 'right',
      cell: (r) => <QualityBadge quality={r.quality} />,
    },
    {
      key: 'action',
      header: 'Aktion',
      align: 'right',
      cell: (r) => (
        <button
          type="button"
          onClick={() => onTrack?.(r.id)}
          className="rounded border border-amber-500/30 bg-amber-500/15 px-2 py-0.5 text-[11px] text-amber-300 hover:bg-amber-500/25"
        >
          👁 Tracken
        </button>
      ),
      toggleable: false,
    },
  ];

  return (
    <div className="space-y-3">
      <DataTable
        rows={filteredRows}
        columns={columns}
        rowKey={(r) => r.id}
        title={`Alle ${coin}-Positionen weltweit`}
        subtitle={
          subtitle ??
          (totalWallets
            ? `Scanned aus clearinghouseState · ${totalWallets.toLocaleString('en-US')} Wallets`
            : 'Scanned aus clearinghouseState')
        }
        search={{ value: search, onChange: setSearch, placeholder: 'Wallet/Label…' }}
        toolbar={
          onScan ? (
            <button
              type="button"
              onClick={onScan}
              className="flex items-center gap-1.5 rounded-md bg-amber-500 px-3 py-1.5 text-[12px] font-bold text-neutral-950 hover:bg-amber-400"
            >
              ▶ Scan starten
            </button>
          ) : undefined
        }
        loading={loading}
        emptyState={
          <div>
            <div>Noch keine Positionen gescanned.</div>
            <div className="mt-1 text-[11px]">
              Klick „Scan starten" um die Top-Wallets durchzugehen.
            </div>
          </div>
        }
        footer={
          rows.length > 0 ? (
            <div className="flex items-center justify-between">
              <span>
                {selected.size} von {rows.length} ausgewählt
              </span>
              <div className="flex items-center gap-3">
                <span>Seite 1 / 1</span>
                <button type="button" className="hover:text-neutral-300">
                  ← Prev
                </button>
                <button type="button" className="hover:text-neutral-300">
                  Next →
                </button>
              </div>
            </div>
          ) : undefined
        }
      />
    </div>
  );
}

function filterRows<T extends HlPositionRow>(rows: ReadonlyArray<T>, q: string): T[] {
  if (!q.trim()) return [...rows];
  const lower = q.toLowerCase();
  return rows.filter(
    (r) =>
      r.address.toLowerCase().includes(lower) ||
      (r.label ? r.label.toLowerCase().includes(lower) : false),
  );
}

function fmt(value: number): string {
  return value.toLocaleString('en-US', { maximumFractionDigits: 2 });
}

function fmtBig(value: number): string {
  const abs = Math.abs(value);
  if (abs >= 1_000_000_000) return `$${(value / 1_000_000_000).toFixed(2)}B`;
  if (abs >= 1_000_000) return `$${(value / 1_000_000).toFixed(2)}M`;
  if (abs >= 1_000) return `$${(value / 1_000).toFixed(0)}K`;
  return `$${value.toFixed(0)}`;
}

function shortAddr(addr: string): string {
  if (addr.length < 12) return addr;
  return `${addr.slice(0, 8)}…${addr.slice(-2)}`;
}
